import type { QueryClient } from '@tanstack/react-query';

/**
 * Identity-transition cache policy — web side (mobile equivalent:
 * `libs/frontend/mobile/hooks/src/auth/resetSessionCache.ts`).
 *
 * Why this is NOT the api-hooks invalidation layer: auth transitions
 * (sign-in, org-switch, logout) change *who the user is*, so every
 * cached query is stale at once — entity-scoped invalidation doesn't
 * apply, and token persistence must land *before* any refetch (the
 * api-hooks layer runs invalidation before `onSuccess` and knows
 * nothing about the token store). The auth API hooks therefore set
 * `invalidationStrategy: 'none'` and the managers call this helper
 * at the right point in the sequence.
 *
 * Policy:
 *  - `sign-in` / `org-switch` → `invalidateQueries()`: keep the tree
 *    mounted (and any just-seeded `current-user`) while everything
 *    refetches under the new identity. Clearing on org-switch used
 *    to briefly drop the user → `posthog.reset()` mid-session.
 *  - `sign-out` → `clear()`: drop the entire cache (including the
 *    persisted copy) so the next session cannot see prior data.
 */
export function resetSessionCache(
  queryClient: QueryClient,
  transition: 'sign-in' | 'org-switch' | 'sign-out',
): Promise<void> | void {
  if (transition === 'sign-in' || transition === 'org-switch') {
    return queryClient.invalidateQueries();
  }
  queryClient.clear();
}
